/**CORE LIBRARIES IMPORT */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

/**COMPONENTS IMPORT */
import SectionHeading from "../Components/SectionHeading";
import SumUpCardWidget from "../Components/SumUpCardWidget";
import TicketQr from "../Components/TicketQr";

/**DATA / HOOKS / SERVICES IMPORT */
import { getEventBySlug, formatEventDate, formatPrice } from "../data/events";
import useSiteSettings from "../hooks/useSiteSettings";
import usePageTitle from "../hooks/usePageTitle";
import { useResolvedEvent } from "../hooks/useContent";
import { formatStartTime } from "../services/content";
import {
  getBooking,
  simulateTestPayment,
  recallBookingAccess,
  bookingErrorMessage,
  getBookingMode,
} from "../services/booking";

const money = (cents, currency) => formatPrice({ amount: cents / 100, currency });

/** Email wording follows the server's real delivery state: no promise unless it is sent or queued. */
export function confirmationEmailText(state) {
  if (state === "sent") return "A confirmation email with your booking details has been sent to you.";
  if (state === "pending") return "Your confirmation email is on its way.";
  return "Please keep a note of your booking reference above.";
}

const STATUS_COPY = {
  pending_payment: { title: "Complete Your Payment", text: "Your places are held while you complete payment." },
  confirmed: { title: "Booking Confirmed", text: "Thank you! Your booking is confirmed." },
  cancelled: { title: "Booking Not Completed", text: "This booking was not completed and no places are held." },
  expired: { title: "Booking Expired", text: "The time to complete payment ran out and the places were released." },
  refunded: { title: "Booking Refunded", text: "This booking has been refunded." },
};

/**
 * Booking status + payment step. Status always comes from the server
 * (or the dev simulator). "Confirmed" is shown only after verification.
 */
const BookingStatus = () => {
  const contactEmail = useSiteSettings().email;
  const { reference } = useParams();
  usePageTitle(`Booking ${reference}`);
  const access = recallBookingAccess(reference);
  // Static or portal-created event; the booking itself is always loaded from the server.
  const { event: resolved } = useResolvedEvent(access?.eventSlug);
  const event = resolved || (access ? { slug: access.eventSlug } : null);
  const mode = getBookingMode();

  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!access || !event) return;
    try {
      setBooking(await getBooking(event, reference, access.accessToken));
      setError(null);
    } catch (err) {
      setError(bookingErrorMessage(err));
    }
  }, [access?.accessToken, access?.eventSlug, reference]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
  }, [refresh]);

  // While payment is pending, re-check with the server periodically.
  useEffect(() => {
    if (booking?.status !== "pending_payment" || mode !== "backend") return;
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [booking?.status, mode, refresh]);

  const simulate = async (outcome) => {
    setBusy(true);
    try {
      setBooking(await simulateTestPayment(event, reference, access.accessToken, outcome));
    } catch (err) {
      setError(bookingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!access || !event) {
    return (
      <main className="simple-page">
        <p className="page-eyebrow">Booking {reference}</p>
        <h1 className="page-title">Booking status</h1>
        <p className="section-text">
          Please open this page on the device you booked on.
          Questions? Email <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
        </p>
      </main>
    );
  }

  const copy = booking ? STATUS_COPY[booking.status] : null;
  const isTest = booking?.payment_mode === "test";
  // SumUp (sandbox or live) shows SumUp's secure card form; only simulated
  // checkouts show the test buttons.
  const isSumUp = booking?.payment?.provider === "sumup";

  return (
    <main className="simple-page booking-status">
      {isTest && (
        <p className="dev-banner checkout-banner" role="note">
          TEST BOOKING: no real payment is taken
          {mode === "simulation" ? " (development simulation in this browser)" : isSumUp ? " (SumUp sandbox: use SumUp test card details)" : ""}.
        </p>
      )}
      <p className="page-eyebrow">Booking reference</p>
      <h1 className="page-title booking-ref">{reference}</h1>

      {error && <p className="checkout-error" role="alert">{error}</p>}
      {!booking && !error && <p className="section-text" role="status">Loading your booking…</p>}

      {booking && (
        <div className="menu-card" aria-live="polite">
          <SectionHeading title={copy?.title || booking.status} />
          <p className="section-text">{copy?.text}</p>
          {booking.status === "confirmed" && <p className="section-text">{confirmationEmailText(booking.confirmation_email)}</p>}

          <dl className="details-list">
            <div className="details-row"><dt>Event</dt><dd>{event.title || booking.event?.title}</dd></div>
            <div className="details-row"><dt>Date</dt><dd>{formatEventDate(event.date || booking.event?.date)}{(event.startTime || formatStartTime(booking.event?.start_time)) && `, ${event.startTime || formatStartTime(booking.event?.start_time)}`}</dd></div>
            <div className="details-row"><dt>Guests</dt><dd>{booking.guests}</dd></div>
            <div className="details-row"><dt>Total</dt><dd>{money(booking.total_cents, booking.currency)} (food menu)</dd></div>
            {booking.status === "pending_payment" && booking.hold_expires_at && (
              <div className="details-row">
                <dt>Places held until</dt>
                <dd>{new Date(booking.hold_expires_at).toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" })}</dd>
              </div>
            )}
          </dl>

          {booking.status === "confirmed" && booking.tickets?.length > 0 && (
            <section className="ticket-list" aria-label="Your tickets">
              <p className="event-menu-heading">Your tickets</p>
              <p className="details-note">
                One QR code per guest. Each admits one person, once. Share a ticket with each guest so
                everyone can arrive separately; anyone holding a ticket can use that place.
              </p>
              <div className="ticket-grid">
                {booking.tickets.map((t) => (
                  <TicketQr key={t.seq} url={t.url} seq={t.seq} count={booking.tickets.length}
                    eventTitle={event.title || booking.event?.title} reference={reference}
                    when={formatEventDate(event.date || booking.event?.date)} />
                ))}
              </div>
            </section>
          )}
          {booking.status === "confirmed" && !booking.tickets?.length && (
            <p className="details-note">Your tickets (QR codes) are in your confirmation email. Lost them? <Link to="/ticket">Resend my tickets</Link>.</p>
          )}

          {booking.status === "pending_payment" && (
            <div className="payment-step">
              {!isSumUp ? (
                <div className="test-payment-panel">
                  <p className="event-menu-heading">Test payment</p>
                  <p className="details-note">Choose an outcome to test the flow. No card is charged.</p>
                  <div className="event-actions">
                    <button type="button" className="hero-btn hero-btn-active" disabled={busy} onClick={() => simulate("paid")}>Simulate Successful Payment</button>
                    <button type="button" className="hero-btn" disabled={busy} onClick={() => simulate("failed")}>Simulate Failed Payment</button>
                    <button type="button" className="hero-btn" disabled={busy} onClick={() => simulate("cancelled")}>Cancel</button>
                  </div>
                </div>
              ) : booking.payment?.checkout_id ? (
                <SumUpCardWidget checkoutId={booking.payment.checkout_id} onFinished={refresh} />
              ) : null}
            </div>
          )}

          {event.drinks && <p className="details-note">{event.drinks.note}</p>}
          <div className="event-actions">
            {getEventBySlug(event.slug) ? (
              <Link to={`/events/${event.slug}`} className="hero-btn">Back to Event</Link>
            ) : (
              <Link to="/" className="hero-btn">Back to Home</Link>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default BookingStatus;
